import type { NotificationPreferences } from '$types/pushNotifications';
import { checkSubscriptionStatus } from '$lib/firebase/fcm';
import { SharedState } from '$utils/sharedState.svelte';
import { getMetriAddress } from '$utils/getMetriAddress';
import { getPayAddress } from '$utils/getPayAddress';

interface SubscriptionData {
  isSubscribed: boolean;
  preferences: NotificationPreferences | null;
}

interface SubscriptionStatusState {
  wallet: SubscriptionData;
  gnosisPay: SubscriptionData;
}

class StateSubscriptionStatus extends SharedState<SubscriptionStatusState> {
  public async load(): Promise<void> {
    const payAddress = getPayAddress();
    const [walletResponse, gnosisPayResponse] = await Promise.all([
      checkSubscriptionStatus(getMetriAddress()),
      payAddress ? checkSubscriptionStatus(payAddress) : Promise.resolve(null),
    ]);

    this.set({
      wallet: {
        isSubscribed: Boolean(walletResponse?.hasSubscription),
        preferences: walletResponse?.preferences || null,
      },
      gnosisPay: {
        isSubscribed: Boolean(gnosisPayResponse?.hasSubscription),
        preferences: gnosisPayResponse?.preferences || null,
      },
    });
  }

  public isWalletSubscribed(): boolean {
    return this.current?.wallet?.isSubscribed ?? false;
  }

  public isGnosisPaySubscribed(): boolean {
    return this.current?.gnosisPay?.isSubscribed ?? false;
  }

  public getWalletPreferences(): NotificationPreferences | null {
    return this.current?.wallet?.preferences || null;
  }

  public getGnosisPayPreferences(): NotificationPreferences | null {
    return this.current?.gnosisPay?.preferences || null;
  }

  public updateSubscription(
    address: string,
    isSubscribed: boolean,
    preferences: NotificationPreferences | null,
  ): void {
    const currentState = this.current;
    if (!currentState) return;

    const payAddress = getPayAddress();
    const metriAddress = getMetriAddress();

    if (address === metriAddress) {
      this.set({
        ...currentState,
        wallet: {
          isSubscribed,
          preferences,
        },
      });
    } else if (address === payAddress) {
      this.set({
        ...currentState,
        gnosisPay: {
          isSubscribed,
          preferences,
        },
      });
    }
  }
}

export const subscriptionStatus = new StateSubscriptionStatus(null);
